// src/services/api.js - COMPLETE FIXED VERSION
import axios from 'axios';

// ================================
// AXIOS INSTANCE CONFIGURATION
// ================================

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:5000/api',
  withCredentials: true,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// ✅ FIXED: Request interceptor - Only add token if available
api.interceptors.request.use(
  (config) => {
    // Only add auth token if it exists (don't force it for public routes)
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp to prevent caching for GET requests
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }

    // Log request in development
   

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ✅ FIXED: Response interceptor - No automatic redirects
api.interceptors.response.use(
  (response) => {
    // Log response in development
   
    return response;
  },
  (error) => {
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data);
    }

    // ✅ FIXED: Handle errors without automatic token clearing
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // ✅ Only log warning, don't auto-clear tokens for public pages
          console.warn('401 Unauthorized:', data?.message || 'Authentication required');
          break;
        case 403:
          console.error('403 Forbidden:', data?.message || 'Access denied');
          break;
        case 404:
          console.error('404 Not Found:', data?.message || 'Resource not found');
          break;
        case 422:
          console.error('422 Validation Error:', data?.errors || data?.message);
          break;
        case 429:
          console.error('429 Rate Limit:', 'Too many requests');
          break;
        case 500:
          console.error('500 Server Error:', data?.message || 'Internal server error');
          break;
        default:
          console.error(`${status} API Error:`, data?.message || 'Unknown error');
      }
    } else if (error.request) {
      console.error('Network error - no response received');
    } else {
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

// ================================
// UTILITY FUNCTIONS
// ================================

export const handleApiError = (error) => {
  if (error.response) {
    return error.response.data.message || 'An error occurred';
  } else if (error.request) {
    return 'Network error - please check your connection';
  } else {
    return 'An unexpected error occurred';
  }
};

export const formatApiResponse = (response) => {
  return {
    success: true,
    data: response.data,
    message: response.message || 'Success'
  };
};

// Helper function to create FormData for file uploads
const createFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (key === 'images' && Array.isArray(data[key])) {
      data[key].forEach((image, index) => {
        formData.append('images', image);
      });
    } else if (Array.isArray(data[key])) {
      formData.append(key, JSON.stringify(data[key]));
    } else if (data[key] !== undefined && data[key] !== null) {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

// ================================
// AUTH API SERVICES
// ================================

export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Login user - manage tokens explicitly
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // Logout user - clear tokens explicitly
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Get user profile - handle 401 specifically
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      // ✅ Only clear tokens for auth-specific errors
      if (error.response?.status === 401) {
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    const response = await api.put('/auth/profile', userData);
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (token, password) => {
    const response = await api.put(`/auth/reset-password/${token}`, { password });
    return response.data;
  },

  // Verify email
  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  // Resend verification email
  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  }
};

// ================================
// PROPERTY API SERVICES - NO AUTH REQUIRED
// ================================

export const propertyAPI = {
  // Get all properties with filters - PUBLIC
  // In api.js - propertyAPI section

  // ✅ FIXED: Use correct public endpoint
  getAll: async (params = {}) => {
    const response = await api.get('/properties', { params }); // Public endpoint
    return response.data;
  },
  
  // Other methods remain the same...



  // Get property by ID - PUBLIC
  getById: async (id) => {
    const response = await api.get(`/properties/${id}`);
    return response.data;
  },

  // Get featured properties - PUBLIC
  getFeatured: async (limit = 6) => {
    const response = await api.get(`/properties/featured`);
    return response.data;
  },

  // Search properties - PUBLIC
  search: async (query, filters = {}) => {
    const response = await api.get('/properties/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  // Create new property - REQUIRES AUTH
  create: async (propertyData) => {
    const hasFiles = propertyData.images && propertyData.images.length > 0;
    if (hasFiles) {
      const formData = createFormData(propertyData);
      const response = await api.post('/properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else {
      const response = await api.post('/properties', propertyData);
      return response.data;
    }
  },

  // Update property - REQUIRES AUTH
  update: async (id, propertyData) => {
    const hasFiles = propertyData.images && propertyData.images.length > 0;
    if (hasFiles) {
      const formData = createFormData(propertyData);
      const response = await api.put(`/properties/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } else {
      const response = await api.put(`/properties/${id}`, propertyData);
      return response.data;
    }
  },

  // Delete property - REQUIRES AUTH
  delete: async (id) => {
    const response = await api.delete(`/properties/${id}`);
    return response.data;
  },

  // Toggle favorite property - REQUIRES AUTH
  toggleFeatured: async (id) => {
    const response = await api.post(`/properties/${id}/favorite`);
    return response.data;
  },

  // Get user's favorite properties - REQUIRES AUTH
  getFavorites: async () => {
    const response = await api.get('/properties/favorites');
    return response.data;
  },

  // Get property statistics - PUBLIC
  getStats: async () => {
    const response = await api.get('/properties/stats');
    return response.data;
  },

  // Get similar properties - PUBLIC
  getSimilar: async (id, limit = 4) => {
    const response = await api.get(`/properties/${id}/similar?limit=${limit}`);
    return response.data;
  },

  // Get properties by agent - PUBLIC
  getByAgent: async (agentId, params = {}) => {
    const response = await api.get(`/properties/agent/${agentId}`, { params });
    return response.data;
  },

  // Get properties by owner - REQUIRES AUTH
  getByOwner: async (params = {}) => {
    const response = await api.get('/properties/my-properties', { params });
    return response.data;
  }
};

// ================================
// AGENT API SERVICES - MOSTLY PUBLIC
// ================================

export const agentAPI = {
  // Get all agents with filters - PUBLIC
  getAll: async (params = {}) => {
    const response = await api.get('/agents', { params });
    return response.data;
  },

  // Get agent by ID - PUBLIC
  getById: async (id) => {
    const response = await api.get(`/agents/${id}`);
    return response.data;
  },

  // Get agent's properties - PUBLIC
  getProperties: async (id, params = {}) => {
    const response = await api.get(`/agents/${id}/properties`, { params });
    return response.data;
  },

  // Get agent statistics - PUBLIC
  getStats: async (id) => {
    const response = await api.get(`/agents/${id}/stats`);
    return response.data;
  },

  // Contact agent - REQUIRES AUTH
  contact: async (id, messageData) => {
    const response = await api.post(`/agents/${id}/contact`, messageData);
    return response.data;
  },

  // Get featured agents - PUBLIC
  getFeatured: async (limit = 4) => {
    const response = await api.get(`/agents/featured?limit=${limit}`);
    return response.data;
  },

  // Search agents - PUBLIC
  search: async (query, filters = {}) => {
    const response = await api.get('/agents/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  // Get agent reviews - PUBLIC
  getReviews: async (id, params = {}) => {
    const response = await api.get(`/agents/${id}/reviews`, { params });
    return response.data;
  },

  // Create agent review - REQUIRES AUTH
  createReview: async (agentId, reviewData) => {
    const response = await api.post(`/agents/${agentId}/reviews`, reviewData);
    return response.data;
  }
};

// ================================
// BLOG API SERVICES - MOSTLY PUBLIC
// ================================



export const blogAPI = {
  // ===== PUBLIC BLOG ROUTES =====

  // Get all public blog posts with filters
  // Backend: GET /api/blogs?category=&search=&sortBy=&sortOrder=&page=&limit=
  getAll: async (params = {}) => {
    const response = await api.get('/blogs', { params });
    // typical response: { success, data: { blogs, pagination } }
    return response.data;
  },

  // Get single public blog by slug
  // Backend: GET /api/blogs/:slug
  getBySlug: async (slug) => {
    const response = await api.get(`/blogs/${slug}`);
    // typical response: { success, data: blog }
    return response.data;
  },

  // Get featured public blog posts
  // Backend: GET /api/blogs/featured?limit=3
  getFeatured: async (limit = 3) => {
    const response = await api.get(`/blogs/featured`, {
      params: { limit }
    });
    return response.data;
  },

  // Get recent public blog posts
  // Backend: GET /api/blogs/recent?limit=5
  getRecent: async (limit = 5) => {
    const response = await api.get(`/blogs/recent`, {
      params: { limit }
    });
    return response.data;
  },

  // Get blog categories (optional public endpoint)
  // Backend: GET /api/blogs/categories
  getCategories: async () => {
    const response = await api.get('/blogs/categories');
    return response.data;
  },

  // Get blog posts by category
  // Backend: GET /api/blogs/category/:category
  getByCategory: async (category, params = {}) => {
    const response = await api.get(`/blogs/category/${category}`, { params });
    return response.data;
  },

  // Search blog posts
  // Backend: GET /api/blogs/search?q=&...
  search: async (query, filters = {}) => {
    const response = await api.get('/blogs/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  // ===== ADMIN BLOG ROUTES (require auth) =====

  // List all blogs (admin view, includes drafts etc.)
  getAllAdmin: async (params = {}) => {
    const response = await api.get('/admin/blogs', { params });
    return response.data;
  },

  // Get blog by ID (admin)
  getByIdAdmin: async (id) => {
    const response = await api.get(`/admin/blogs/${id}`);
    return response.data;
  },

  // Create blog (admin)
  create: async (postData) => {
    const response = await api.post('/admin/blogs', postData);
    return response.data;
  },

  // Update blog (admin)
  update: async (id, postData) => {
    const response = await api.put(`/admin/blogs/${id}`, postData);
    return response.data;
  },

  // Delete blog (admin)
  delete: async (id) => {
    const response = await api.delete(`/admin/blogs/${id}`);
    return response.data;
  },

  // Like/unlike blog post (optional public or auth route if you add it)
  toggleLike: async (id) => {
    const response = await api.post(`/blogs/${id}/like`);
    return response.data;
  },

  // Comments (if you add public comment routes later)
  addComment: async (id, commentData) => {
    const response = await api.post(`/blogs/${id}/comments`, commentData);
    return response.data;
  },

  getComments: async (id, params = {}) => {
    const response = await api.get(`/blogs/${id}/comments`, { params });
    return response.data;
  }
};


// ================================
// CONTACT API SERVICES
// ================================

export const contactAPI = {
  // Send contact message - PUBLIC
  sendMessage: async (messageData) => {
    const response = await api.post('/contact', messageData);
    return response.data;
  },

  // Subscribe to newsletter - PUBLIC
  subscribe: async (email) => {
    const response = await api.post('/contact/newsletter', { email });
    return response.data;
  },

  // Unsubscribe from newsletter - PUBLIC
  unsubscribe: async (email) => {
    const response = await api.post('/contact/unsubscribe', { email });
    return response.data;
  },

  // Request property valuation - PUBLIC
  requestValuation: async (valuationData) => {
    const response = await api.post('/contact/valuation', valuationData);
    return response.data;
  },

  // Schedule property viewing - PUBLIC
  scheduleViewing: async (viewingData) => {
    const response = await api.post('/contact/viewing', viewingData);
    return response.data;
  },

  // Request callback - PUBLIC
  requestCallback: async (callbackData) => {
    const response = await api.post('/contact/callback', callbackData);
    return response.data;
  },

  // Send inquiry about property - PUBLIC
  sendPropertyInquiry: async (propertyId, inquiryData) => {
    const response = await api.post(`/contact/property/${propertyId}/inquiry`, inquiryData);
    return response.data;
  }
};

// ================================
// UPLOAD API SERVICES - REQUIRES AUTH
// ================================

export const uploadAPI = {
  // Upload single file
  uploadFile: async (file, folder = 'general', onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await api.post('https://client-backend-nvaq.onrender.com/api/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    });
    return response.data;
  },

  // Upload multiple files
  uploadFiles: async (files, folder = 'general', onProgress = null) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file);
    });
    formData.append('folder', folder);

    const response = await api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    });
    return response.data;
  },

  // Delete file
  deleteFile: async (fileUrl) => {
    const response = await api.delete('/upload/delete', {
      data: { fileUrl }
    });
    return response.data;
  }
};

// ================================
// ADMIN API SERVICES - ALL REQUIRE AUTH
// ================================

export const adminAPI = {
  // Dashboard & Analytics
  getDashboardStats: () => api.get('/admin/dashboard-stats'),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  getPerformanceMetrics: () => api.get('/admin/metrics'),
  getSystemHealth: () => api.get('/admin/health'),

  // User Management
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  bulkUpdateUsers: (data) => api.post('/admin/users/bulk-update', data),
  bulkDeleteUsers: (ids) => api.post('/admin/users/bulk-delete', { ids }),

  // Property Management
  getProperties: (params) => api.get('/admin/properties', { params }),
  createProperty: (data) => api.post('/admin/properties', data),
  updateProperty: (id, data) => api.put(`/admin/properties/${id}`, data),
  deleteProperty: (id) => api.delete(`/admin/properties/${id}`),
  updatePropertyStatus: (id, status) => api.put(`/admin/properties/${id}/status`, { status }),
  togglePropertyFeatured: (id) => api.put(`/admin/properties/${id}/toggle-featured`),

  // Agent Management
  getAgents: (params) => api.get('/admin/agents', { params }),
  createAgent: (data) => api.post('/admin/agents', data),
  updateAgent: (id, data) => api.put(`/admin/agents/${id}`, data),
  deleteAgent: (id) => api.delete(`/admin/agents/${id}`),

  // Blog Management
  getBlogs: (params) => api.get('/admin/blogs', { params }),
  createBlog: (data) => api.post('/admin/blogs', data),
  updateBlog: (id, data) => api.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id) => api.delete(`/admin/blogs/${id}`),

  // Contact Management
  getContacts: (params) => api.get('/admin/contacts', { params }),
  getContact: (id) => api.get(`/admin/contacts/${id}`),
  updateContactStatus: (id, status) => api.put(`/admin/contacts/${id}/status`, { status }),
  deleteContact: (id) => api.delete(`/admin/contacts/${id}`),

  // AI Services
  generatePropertyDescription: (data) => api.post('/admin/ai/generate-property-description', data),
  generateContent: (type, prompt, options = {}) => api.post('/admin/ai/generate-content', { type, prompt, options }),

  // System Administration
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),
  getLogs: (params) => api.get('/admin/logs', { params }),
  getSystemStatus: () => api.get('/admin/system-status')
};

// ================================
// SEARCH API SERVICES - PUBLIC
// ================================

export const searchAPI = {
  // Global search - PUBLIC
  global: async (query, filters = {}) => {
    const response = await api.get('/search', {
      params: { q: query, ...filters }
    });
    return response.data;
  },

  // Search suggestions - PUBLIC
  suggestions: async (query, type = 'all') => {
    const response = await api.get('/search/suggestions', {
      params: { q: query, type }
    });
    return response.data;
  },

  // Popular searches - PUBLIC
  popular: async (limit = 10) => {
    const response = await api.get(`/search/popular?limit=${limit}`);
    return response.data;
  },

  // Save search - REQUIRES AUTH
  saveSearch: async (searchData) => {
    const response = await api.post('/search/save', searchData);
    return response.data;
  },

  // Get saved searches - REQUIRES AUTH
  getSavedSearches: async () => {
    const response = await api.get('/search/saved');
    return response.data;
  },

  // Delete saved search - REQUIRES AUTH
  deleteSavedSearch: async (id) => {
    const response = await api.delete(`/search/saved/${id}`);
    return response.data;
  }
};

// ================================
// NOTIFICATION API SERVICES - REQUIRES AUTH
// ================================

export const notificationAPI = {
  // Get user notifications
  getAll: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  // Delete notification
  delete: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  // Get notification settings
  getSettings: async () => {
    const response = await api.get('/notifications/settings');
    return response.data;
  },

  // Update notification settings
  updateSettings: async (settings) => {
    const response = await api.put('/notifications/settings', settings);
    return response.data;
  }
};

// ================================
// MAIN API EXPORT
// ================================

export default api;
